import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { Loader2, Maximize2 } from "lucide-react";

import { useBlsHaptics } from "../../application/useBlsHaptics";
import { useRelayBlsChannel } from "../../application/useRelayBlsChannel";
import { joinPublicBoard } from "../../application/publicBoard";
import PatientBlsView from "../components/PatientBlsView";

type Load = { kind: "loading" } | { kind: "ready"; cursor: number } | { kind: "refused"; messageKey: string };

/** What a refused link means, in the patient's words. */
function refusalKey(error: unknown): string {
    if (isAxiosError(error)) {
        if (error.response?.status === 404) return "emdr.remote.notFound";
        if (error.response?.status === 410) return "emdr.remote.expired";
    }
    return "emdr.remote.unavailable";
}

/**
 * A therapist's EMDR board, opened from the link they sent.
 *
 * No account, no camera, no microphone: the stimulus, the stop button, and
 * nothing to look at besides. The patient may be talking to their therapist
 * on the phone or on another video platform in the meantime. The set reaches
 * this page through a mailbox on our server, read every second.
 */
export default function EmdrRemotePatientPage() {
    const { t } = useTranslation();
    const { token = "" } = useParams<{ token: string }>();
    const [load, setLoad] = useState<Load>({ kind: "loading" });

    useEffect(() => {
        let stale = false;
        joinPublicBoard(token)
            .then(({ cursor }) => !stale && setLoad({ kind: "ready", cursor }))
            .catch((error) => !stale && setLoad({ kind: "refused", messageKey: refusalKey(error) }));
        return () => {
            stale = true;
        };
    }, [token]);

    return (
        <div translate="no" className="room-surface flex min-h-dvh w-full flex-col bg-room-base text-room-ink" data-testid="emdr-remote-patient">
            {load.kind === "loading" && (
                <p role="status" className="m-auto flex items-center gap-2 text-sm text-room-ink-muted">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t("emdr.remote.connecting")}
                </p>
            )}
            {load.kind === "refused" && (
                <p role="alert" className="m-auto max-w-sm p-6 text-center text-sm text-room-ink-muted">
                    {t(load.messageKey)}
                </p>
            )}
            {load.kind === "ready" && <RemoteStimulus token={token} cursor={load.cursor} />}
        </div>
    );
}

/** Once in: the set when there is one, and the wait before it. */
function RemoteStimulus({ token, cursor }: { token: string; cursor: number }) {
    const { t } = useTranslation();
    const channel = useRelayBlsChannel(token, cursor);
    const haptics = useBlsHaptics({ state: channel.state, cyclesNow: channel.cyclesNow });
    const { reportControllers } = channel;

    useEffect(() => {
        reportControllers(haptics.controllers);
    }, [haptics.controllers, reportControllers]);

    if (channel.state) {
        return (
            <div className="min-h-0 flex-1">
                <PatientBlsView channel={channel} haptics={haptics} />
            </div>
        );
    }

    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
            <p role="status" className="text-sm text-room-ink-muted">
                {t("emdr.board.waiting")}
            </p>
            <button
                type="button"
                onClick={() => void document.documentElement.requestFullscreen?.().catch(() => undefined)}
                className="room-control"
            >
                <Maximize2 size={16} aria-hidden="true" />
                <span>{t("emdr.board.fullscreen")}</span>
            </button>
        </div>
    );
}
